import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { adminApiService } from '../../services/adminApi';
import CustomDropdown from '../CustomDropdown';

const UserManagement = () => {
  const { adminUser } = useAdmin();
  const [users, setUsers] = useState([]);
  const [courtTypes, setCourtTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: ''
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [showTokensModal, setShowTokensModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tokensForm, setTokensForm] = useState({
    courtTypeId: '',
    tokens: 0,
    action: 'add'
  });
  
  const observerTarget = useRef(null);
  const isResetting = useRef(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [filters.search]);

  const fetchCourtTypes = useCallback(async () => {
    try {
      const data = await adminApiService.getCourtTypes(adminUser.idToken);
      setCourtTypes(data);
    } catch (err) {
      setError(err.message);
    }
  }, [adminUser.idToken]);

  const fetchUsers = useCallback(async (page = 1, resetUsers = false) => {
    try {
      const params = {
        search: debouncedSearch,
        page: page
      };
      const data = await adminApiService.getUsers(adminUser.idToken, params);
      
      if (resetUsers) {
        setUsers(data.users || []);
      } else {
        setUsers(prev => [...prev, ...(data.users || [])]);
      }
      
      setPagination(data.pagination);
      setCurrentPage(page);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [adminUser.idToken, debouncedSearch]);

  const fetchData = useCallback(async (page = 1, resetUsers = false) => {
    try {
      if (resetUsers) {
        setLoading(true);
        isResetting.current = true;
      }
      await fetchUsers(page, resetUsers);
    } catch (err) {
      setError(err.message);
    } finally {
      if (resetUsers) {
        setLoading(false);
        // Small delay to prevent immediate infinite scroll trigger
        setTimeout(() => {
          isResetting.current = false;
        }, 100);
      }
    }
  }, [fetchUsers]);

  const loadMoreUsers = useCallback(async () => {
    if (!pagination?.hasNextPage || loadingMore) return;
    
    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      await fetchUsers(nextPage, false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }, [pagination, loadingMore, currentPage, fetchUsers]);

  // Fetch court types on mount
  useEffect(() => {
    fetchCourtTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset to page 1 when search changes
  useEffect(() => {
    setUsers([]);
    setCurrentPage(1);
    setPagination(null);
    fetchData(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Don't trigger if we're resetting/loading initial data
        if (entries[0].isIntersecting && pagination?.hasNextPage && !loadingMore && !loading && !isResetting.current) {
          loadMoreUsers();
        }
      },
      { threshold: 0.5 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination?.hasNextPage, loadingMore, loading]);

  const getCourtTypeName = (courtTypeId) => {
    const courtType = courtTypes.find(ct => ct.id === courtTypeId);
    return courtType ? courtType.name : courtTypeId; // Fallback to ID if not found
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: ''
    });
  };


  const handleTokensUpdate = async (e) => {
    e.preventDefault();
    try {
      await adminApiService.updateUserTokens(
        adminUser.idToken,
        selectedUser.id,
        tokensForm.courtTypeId,
        tokensForm.tokens,
        tokensForm.action
      );
      setShowTokensModal(false);
      setSelectedUser(null);
      const defaultCourtTypeId = courtTypes.length > 0 ? courtTypes[0].id : '';
      setTokensForm({ courtTypeId: defaultCourtTypeId, tokens: 0, action: 'add' });
      // Refresh the user list
      setUsers([]);
      setCurrentPage(1);
      setPagination(null);
      fetchData(1, true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAdminToggle = async (user) => {
    try {
      await adminApiService.toggleUserAdmin(adminUser.idToken, user.id, !user.isAdmin);
      // Refresh the user list
      setUsers([]);
      setCurrentPage(1);
      setPagination(null);
      fetchData(1, true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStaffToggle = async (user) => {
    try {
      await adminApiService.toggleUserStaff(adminUser.idToken, user.id, !user.isStaff);
      // Refresh the user list
      setUsers([]);
      setCurrentPage(1);
      setPagination(null);
      fetchData(1, true);
    } catch (err) {
      setError(err.message);
    }
  };


  const openTokensModal = (user) => {
    setSelectedUser(user);
    // Set the first available court type as default
    const defaultCourtTypeId = courtTypes.length > 0 ? courtTypes[0].id : '';
    setTokensForm({ courtTypeId: defaultCourtTypeId, tokens: 0, action: 'add' });
    setShowTokensModal(true);
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp._seconds * 1000);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">ผู้ใช้</h2>
        <div className="text-sm text-gray-500">
          {pagination ? (
            <>
              แสดง {users.length} จาก {pagination.totalUsers} ผู้ใช้
              {pagination.totalPages > 1 && (
                <span className="ml-2 text-xs text-gray-400">
                  (หน้า {pagination.currentPage} / {pagination.totalPages})
                </span>
              )}
            </>
          ) : (
            <>รวม: {users.length} ผู้ใช้</>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className="text-sm font-medium text-gray-900 mb-3">ตัวกรอง</h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">ค้นหาผู้ใช้</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="ค้นหาด้วยรหัสผู้ใช้หรือชื่อ"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            {filters.search && (
              <p className="text-xs text-gray-500 mt-1">
                ค้นหา: {debouncedSearch || filters.search}
              </p>
            )}
          </div>
        </div>
        {filters.search && (
          <div className="mt-4">
            <button
              onClick={clearFilters}
              className="text-sm text-gray-600 hover:text-gray-800 underline"
            >
              ล้างตัวกรองทั้งหมด
            </button>
          </div>
        )}
      </div>

      {/* Users List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {users.map((user) => (
            <li key={user.id} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    {user.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.displayName}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-2xl">👤</span>
                    )}
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
                      {user.isAdmin && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          ผู้ดูแลระบบ
                        </span>
                      )}
                      {user.isStaff && !user.isAdmin && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          พนักงาน
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                      {user.contactData?.nickname && (
                        <span className="flex items-center">
                          <span className="text-gray-400 mr-1">👤</span>
                          <span className="text-gray-700 font-medium">{user.contactData.nickname}</span>
                        </span>
                      )}
                      {user.contactData?.mobilePhone && (
                        <span className="flex items-center">
                          <span className="text-gray-400 mr-1">📱</span>
                          <span className="text-gray-700 font-medium">{user.contactData.mobilePhone}</span>
                        </span>
                      )}
                      <span className="flex items-center">
                        <span className="text-gray-400 mr-1">📅</span>
                        <span>{formatDateTime(user.createdAt)}</span>
                      </span>
                    </div>
                    {/* Tokens Display */}
                    {user.tokens && Object.keys(user.tokens).length > 0 && (
                      <div className="mt-2">
                        <span className="text-sm text-gray-600">เหรียญ: </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Object.entries(user.tokens).map(([courtTypeId, amount]) => (
                            <span key={courtTypeId} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {getCourtTypeName(courtTypeId)}: {amount.toFixed(1)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => openTokensModal(user)}
                    className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                  >
                    จัดการเหรียญ
                  </button>
                  <button
                    onClick={() => handleStaffToggle(user)}
                    className={`text-sm font-medium ${
                      user.isStaff
                        ? 'text-red-600 hover:text-red-900'
                        : 'text-blue-600 hover:text-blue-900'
                    }`}
                  >
                    {user.isStaff ? 'ลบสิทธิ์พนักงาน' : 'ให้สิทธิ์พนักงาน'}
                  </button>
                  <button
                    onClick={() => handleAdminToggle(user)}
                    className={`text-sm font-medium ${
                      user.isAdmin
                        ? 'text-red-600 hover:text-red-900'
                        : 'text-green-600 hover:text-green-900'
                    }`}
                  >
                    {user.isAdmin ? 'ลบสิทธิ์ผู้ดูแลระบบ' : 'ให้สิทธิ์ผู้ดูแลระบบ'}
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
        
        {/* Infinite scroll trigger */}
        {pagination?.hasNextPage && (
          <div ref={observerTarget} className="px-6 py-4 text-center">
            {loadingMore ? (
              <div className="flex justify-center items-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-sm text-gray-600">กำลังโหลดเพิ่มเติม...</span>
              </div>
            ) : (
              <button
                onClick={loadMoreUsers}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                โหลดเพิ่มเติม
              </button>
            )}
          </div>
        )}
        
        {users.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            ไม่พบผู้ใช้ที่ตรงกับตัวกรองปัจจุบัน
          </div>
        )}
      </div>


      {/* Tokens Modal */}
      {showTokensModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                จัดการตเหรียญสำหรับ {selectedUser.displayName}
              </h3>
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  เหรียญปัจจุบัน:
                </p>
                <div className="mt-2 space-y-1">
                  {selectedUser.tokens && Object.keys(selectedUser.tokens).length > 0 ? (
                    Object.entries(selectedUser.tokens).map(([courtTypeId, amount]) => (
                      <div key={courtTypeId} className="flex justify-between text-sm">
                        <span className="capitalize">{getCourtTypeName(courtTypeId)}:</span>
                        <span className="font-medium">{amount.toFixed(1)}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">ไม่มีเหรียญ</p>
                  )}
                </div>
              </div>
              <form onSubmit={handleTokensUpdate}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">ประเภทสนาม</label>
                    <CustomDropdown
                      value={tokensForm.courtTypeId}
                      onChange={(val) => setTokensForm({ ...tokensForm, courtTypeId: val })}
                      placeholder="เลือกประเภทสนาม"
                      options={courtTypes.map((courtType) => ({ value: courtType.id, label: courtType.name }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">การดำเนินการ</label>
                    <CustomDropdown
                      value={tokensForm.action}
                      onChange={(val) => setTokensForm({ ...tokensForm, action: val })}
                      options={[
                        { value: 'add', label: 'เพิ่มเหรียญ' },
                        { value: 'subtract', label: 'หักเหรียญ' },
                        { value: 'set', label: 'กำหนดเหรียญ' },
                      ]}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">จำนวนเหรียญ</label>
                    <input
                      type="number"
                      step="0.1"
                      value={tokensForm.tokens}
                      onChange={(e) => setTokensForm({ ...tokensForm, tokens: parseFloat(e.target.value) })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                      min="0"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowTokensModal(false);
                      setSelectedUser(null);
                      const defaultCourtTypeId = courtTypes.length > 0 ? courtTypes[0].id : '';
                      setTokensForm({ courtTypeId: defaultCourtTypeId, tokens: 0, action: 'add' });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    อัปเดตเหรียญ
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement; 